import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CHECKBOXES_TO_TIMING_TYPE = new Map([
  [JSON.stringify({ always: false, oneRemaining: false, everyFive: false }), 0],
  [JSON.stringify({ always: true, oneRemaining: false, everyFive: false }), 1],
  [JSON.stringify({ always: false, oneRemaining: false, everyFive: true }), 5],
  [JSON.stringify({ always: false, oneRemaining: true, everyFive: false }), 10],
  [JSON.stringify({ always: false, oneRemaining: true, everyFive: true }), 15],
]);

const TIMING_TYPE_TO_CHECKBOXES = {
  0: { always: false, oneRemaining: false, everyFive: false },
  1: { always: true, oneRemaining: false, everyFive: false },
  5: { always: false, oneRemaining: false, everyFive: true },
  10: { always: false, oneRemaining: true, everyFive: false },
  15: { always: false, oneRemaining: true, everyFive: true },
};

// オブジェクトから数値を取得
const getTiming = (state) => {
  return CHECKBOXES_TO_TIMING_TYPE.get(JSON.stringify(state));
};

// 数値からオブジェクトを取得
const getCheckboxState = (timing) => {
  return TIMING_TYPE_TO_CHECKBOXES[timing] || null;
};

const ShareTiming = ({ snsAccount, circle, isReactNativeApp }) => {
  const [checkboxes, setCheckboxes] = useState({
    always: false,
    oneRemaining: false,
    everyFive: false,
  });

  useEffect(() => {
    axios.get(`/api/sns_share_options/${snsAccount.id}`).then((res) => {
      const shareTimingSetting = res.data.timing_type;
      const newState = getCheckboxState(shareTimingSetting);
      if (newState === null) {
        return;
      }
      setCheckboxes(newState);
    });
  }, []);

  const decisionNewTiming = (name, checked) => {
    const newTiming = { ...checkboxes };

    if (name === 'always' && checked) {
      newTiming.always = checked;
      newTiming.oneRemaining = false;
      newTiming.everyFive = false;
    } else if (name !== 'always' && checked) {
      newTiming.always = false;
      newTiming[name] = checked;
    } else {
      newTiming[name] = checked;
    }

    return newTiming;
  };

  const handleChange = (e) => {
    const { name, checked } = e.target;

    const newTiming = decisionNewTiming(name, checked);
    const newTimingType = getTiming(newTiming);

    if (newTimingType === null) {
      return;
    }

    axios
      .patch(`/api/sns_share_options/${snsAccount.id}`, {
        timing_type: newTimingType,
      })
      .then((res) => {
        setCheckboxes(newTiming);
      });
  };

  const deleteSnsAccount = () => {
    if (confirm('SNS連携を解除しますか？再設定には再度連携が必要です。')) {
      const circleId = snsAccount.circle_id;
      const snsAccountId = snsAccount.id;
      const url = `/api/circle/${circleId}/sns_accounts/${snsAccountId}`;

      axios
        .delete(url)
        .then((res) => {
          if (res.status === 200) {
            window.location.href = `/circle/${circleId}/sns_accounts`;
          }
        })
        .catch((error) => {
          alert('エラーが発生しました。もう一度お試しください。');
        });
    }
  };

  // 見た目のみの組み替え。選べる組み合わせの決まり・保存の仕組みは変更していない。
  //   「すべての参加申し込み後にシェア」と「申し込み状況に応じてシェア」は片方だけ
  //   「申し込み状況に応じて」の中の2つは両方えらべる
  //   （この見出し自体には選択の実体が無いため、チェックは付いていない）
  const isConditional = checkboxes.oneRemaining || checkboxes.everyFive;

  return (
    <div className="sns-connected">
      {/* 連携中のアカウント。解除もこの行に置く（アカウントについての操作なので） */}
      <div className="sns-acct">
        <span className="sns-acct__logo">
          <img src="/img/x-logo.svg" alt="" />
        </span>
        <div className="sns-acct__text">
          <p className="sns-acct__name">
            Xアカウント
            <span className="sns-status">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 5 5 9-11" /></svg>
              連携済み
            </span>
          </p>
          {snsAccount.username && <p className="sns-acct__id">@{snsAccount.username}</p>}
        </div>
        <button type="button" className="sns-unlink" onClick={deleteSnsAccount}>
          連携を解除
        </button>
      </div>

      <div className="sns-sectionrow">
        <h2 className="sns-sectionhead">自動シェアの設定</h2>
        <p className="sns-autosave">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5.5" /></svg>
          変更は自動で保存されます
        </p>
      </div>
      <p className="sns-sectionlead">このサークルのイベントに参加申し込みがあった際のシェア方法を選択してください。</p>

      <div className="sns-col">
        <h3 className="sns-col__title">シェアのタイミング</h3>

        <label className={`sns-choice sns-choice--select${checkboxes.always ? ' is-on' : ''}`}>
          <input
            type="checkbox"
            name="always"
            id="always"
            value="1"
            className="sns-check"
            checked={checkboxes.always}
            onChange={handleChange}
          />
          <span className="sns-choice__text">
            <span className="sns-choice__label">すべての参加申し込み後にシェア</span>
            <span className="sns-choice__desc">参加申し込みがあるたびにシェアします。</span>
          </span>
        </label>

        {/* 上の2つは片方だけ。連携前の説明と同じ「または」でその関係を示す */}
        <p className="sns-or">または</p>

        <div className={`sns-choice${isConditional ? ' is-on' : ''}`}>
          <span className="sns-choice__text">
            <span className="sns-choice__label">申し込み状況に応じてシェア</span>
            <span className="sns-choice__desc">以下の条件から1つ以上選択します。</span>
          </span>
          <div className="sns-conds">
            <label className="sns-cond">
              <input
                type="checkbox"
                name="oneRemaining"
                id="oneRemaining"
                value="2"
                className="sns-check"
                checked={checkboxes.oneRemaining}
                onChange={handleChange}
              />
              <span className="sns-cond__text">
                <span className="sns-cond__label">残り1席になったとき</span>
                <span className="sns-cond__desc">空席が出たときも含みます</span>
              </span>
            </label>
            <label className="sns-cond">
              <input
                type="checkbox"
                name="everyFive"
                id="everyFive"
                value="3"
                className="sns-check"
                checked={checkboxes.everyFive}
                onChange={handleChange}
              />
              <span className="sns-cond__text">
                <span className="sns-cond__label">参加者が5人増えるごと</span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareTiming;
